import React from "react";
import { motion } from "framer-motion";
import { HashLink } from "react-router-hash-link";
import { Mail, Phone, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <motion.footer
      className="bg-[#FFF0DC] pt-20 pb-10 border-t border-orange-200"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      {/* Main Grid */}
      <motion.div
        className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 text-gray-800"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.2 } },
        }}
      >
        {/* Brand */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
          }}
        >
          <motion.h3
            whileHover={{ scale: 1.03 }}
            className="font-bold text-2xl mb-4 text-orange-600"
          >
            Quirkle
          </motion.h3>

          <p className="text-sm text-gray-700 leading-relaxed">
            Your trusted platform for online therapy, mental well-being,
            and emotional healing.
          </p>
        </motion.div>

        {/* Explore */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
          }}
        >
          <h3 className="font-semibold text-lg mb-4">Explore</h3>
          <ul className="space-y-3 text-sm">
            {[
              { label: "Home", link: "/#home" },
              { label: "Services", link: "/#services" },
              { label: "About Us", link: "/about" },
              { label: "Contact", link: "/#contact" },
            ].map((item, i) => (
              <motion.li key={i} whileHover={{ x: 4 }}>
                <HashLink smooth to={item.link} className="hover:text-orange-600">
                  {item.label}
                </HashLink>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* About */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
          }}
        >
          <h3 className="font-semibold text-lg mb-4">About Us</h3>
          <ul className="space-y-3 text-sm">
            {[
              { label: "We Are", link: "/about#weare" },
              { label: "Our Vision", link: "/about#vision" },
              { label: "Our Team", link: "/about#team" },
              { label: "Terms & Conditions", link: "/about#terms" },
              { label: "FAQs", link: "/about#faq" },
            ].map((item, i) => (
              <motion.li key={i} whileHover={{ x: 4 }}>
                <HashLink smooth to={item.link} className="hover:text-orange-600">
                  {item.label}
                </HashLink>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Contact */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
          }}
        >
          <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
          <ul className="space-y-3 text-sm">
            <motion.li
              className="flex items-center gap-2"
              whileHover={{ x: 4 }}
            >
              <Phone className="w-4 h-4 text-orange-500" /> +91 98765 43210
            </motion.li>

            <motion.li
              className="flex items-center gap-2"
              whileHover={{ x: 4 }}
            >
              <MapPin className="w-4 h-4 text-orange-500" /> Mumbai, Maharashtra, India
            </motion.li>

            <motion.li
              className="flex items-center gap-2"
              whileHover={{ x: 4 }}
            >
              <Mail className="w-4 h-4 text-orange-500" /> support@quirkle.com
            </motion.li>
          </ul>
        </motion.div>
      </motion.div>

      {/* Bottom Bar */}
      <motion.div
        className="border-t border-gray-300 mt-12 pt-6 text-center text-gray-700 text-sm"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
      >
        © {new Date().getFullYear()}{" "}
        <span className="font-semibold text-orange-600">Quirkle</span>. All Rights Reserved.
      </motion.div>
    </motion.footer>
  );
};

export default Footer;
